/**
 * Combine user-pasted HTML and CSS into a complete `<!doctype html>` document.
 * Accepts either a full HTML document, a `<body>`-only fragment, or just
 * inner-body markup. Self-containment (inlining external resources) happens in
 * a separate pass — see `lib/import-self-contain.ts`.
 */
export function mergeImportedHtml(html: string, css?: string): string {
  const trimmedCss = (css ?? '').trim();
  const styleTag = trimmedCss ? `<style>\n${trimmedCss}\n</style>` : '';
  const trimmedHtml = html.trim();
  const isFullDoc = /^<(?:!doctype\s+html|html)\b/i.test(trimmedHtml);

  if (isFullDoc) {
    if (!styleTag) return trimmedHtml;
    if (/<head\b[^>]*>/i.test(trimmedHtml)) {
      return trimmedHtml.replace(/<head\b([^>]*)>/i, `<head$1>${styleTag}`);
    }
    return trimmedHtml.replace(/<html\b([^>]*)>/i, `<html$1><head>${styleTag}</head>`);
  }
  // Treat as body fragment — wrap with a doctype + Tailwind CDN preload + the CSS.
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Imported</title>
    <script src="https://cdn.tailwindcss.com"></script>
    ${styleTag}
  </head>
  <body>
${trimmedHtml}
  </body>
</html>`;
}

/** Concise summary used as the import system-note. */
export function buildImportNote(contained: {
  inlined: string[];
  stripped: Array<{ kind: 'link' | 'script'; href: string; reason: string }>;
  kept: string[];
}): string {
  const lines = [
    'Imported HTML/CSS as the starting artifact (now self-contained). Refine via chat.',
  ];
  if (contained.inlined.length > 0) {
    lines.push(
      `Inlined ${contained.inlined.length} external ${
        contained.inlined.length === 1 ? 'resource' : 'resources'
      }.`,
    );
  }
  if (contained.kept.length > 0) {
    lines.push(
      `Kept ${contained.kept.length} CSP-allowlisted ${
        contained.kept.length === 1 ? 'resource' : 'resources'
      } (Tailwind, fonts).`,
    );
  }
  if (contained.stripped.length > 0) {
    const detail = contained.stripped
      .slice(0, 5)
      .map((s) => `${s.kind} ${s.href} (${s.reason})`)
      .join('; ');
    const more =
      contained.stripped.length > 5 ? `, +${contained.stripped.length - 5} more` : '';
    lines.push(
      `Stripped ${contained.stripped.length} ${
        contained.stripped.length === 1 ? 'resource' : 'resources'
      }: ${detail}${more}.`,
    );
  }
  return lines.join(' ');
}
